import * as React from 'react';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useGetNotifications, useUpdateNotificationStatus } from '../../../core/api/pushNotification';
import { UserNotification } from '../../../models/UserNotification';
import { redirectNotificationCodes } from '../../../core/constants';
import NotificationListSection, { NotificationItem } from './sections/NotificationListSection';
import { NotificationStatus } from '../../../core/enums';
import { ApiResultMessageModel } from '../../../models/ApiResultMessage';
import BottomModal from '../../../commonComponents/bottomModal/BottomModal';
import { getFormattedDate } from '../../../lib/utils';
import { defaultModalMessage } from '../../../core/domain';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import useClientContextStore from '../../../zustandStore/clientContextStore';
import isUnreadNotification from '../../common/pushNotification/pushNotification';
import { hasValue } from '../../../lib/lib';
import SearchInput from '../../../commonComponents/appForm/SearchInput';
import { NoMarginEmptyCard } from '../common/EmptyCard';

const NotificationSubPage = () => {
	const navigate = useNavigateWithAppName();
	const [searchQuery, setSearchQuery] = React.useState('');
	const [searchActive, setSearchActive] = React.useState(false);
	const [userNotifications, setUserNotifications] = useState<UserNotification[]>();
	const [modalMessage, setModalMessage] = useState<ApiResultMessageModel>({
		title: '',
		description: []
	});
	const refetchClientDetailsFromApi = useClientContextStore((state) => state.refetchClientDetailsFromApi);
	const queryClient = useQueryClient();
	const updateNotificationMutation = useUpdateNotificationStatus(queryClient);
	const cachedNotifications = useGetNotifications(true);

	const handleCloseModal = () => {
		setModalMessage(defaultModalMessage);
	};

	useEffect(() => {
		if (cachedNotifications && userNotifications === undefined) {
			setUserNotifications(cachedNotifications);
		}
	}, [cachedNotifications]);

	const handleViewAlertClick = async (userNotificationId: number) => {
		const notification = userNotifications.find((n) => n.userNotificationId === userNotificationId);

		if (notification) {
			// Don't await for the following 2 async actions, so that can navigate to the next page without waiting
			updateNotificationMutation.mutate({
				userNotificationId,
				status: NotificationStatus.Read
			});
			refetchClientDetailsFromApi();

			if (redirectNotificationCodes.includes(notification.notificationCode) && notification.actionLink) {
				navigate(notification.actionLink);
			} else {
				setModalMessage({
					title: notification.title,
					description: [notification.body],
					actions: notification.actionLink
						? [{ label: notification.actionName, internalLink: notification.actionLink }]
						: null
				});
				// Update the status of the notification in the state
				setUserNotifications((prev) => {
					return prev.map((n) =>
						n.userNotificationId === userNotificationId
							? { ...n, status: NotificationStatus.Read } // Set status to 'Read'
							: n
					);
				});
			}
		}
	};

	const handleDeleteAlert = async (userNotificationId: number) => {
		updateNotificationMutation.mutate({
			userNotificationId,
			status: NotificationStatus.Deleted
		});
		refetchClientDetailsFromApi(); // No need to await to refetch client details for current page
		if (userNotifications.find((n) => n.userNotificationId === userNotificationId))
			setUserNotifications((prev) => {
				return prev.filter((n) => n.userNotificationId !== userNotificationId);
			});
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchQuery(value);
		setSearchActive(value.length >= 3);
	};

	const filteredUserNotifications = React.useMemo(() => {
		if (!userNotifications) return undefined;

		const query = searchActive ? searchQuery.toLowerCase() : '';
		return userNotifications.filter((notification) => {
			if (!searchActive) return true;

			const fields = [notification.title ?? '', notification.body ?? ''];
			return fields.some((field) => field.toLowerCase().includes(query));
		});
	}, [searchActive, searchQuery, userNotifications]);

	return (
		<>
			<StyledSearchInput value={searchQuery} onChange={handleSearchChange} placeholder="Search notifications" />

			{filteredUserNotifications === undefined && (
				<SkeletonList>
					{[0, 1, 2, 3].map((i) => (
						<SkeletonRow key={i}>
							<Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
							<SkeletonRowContent>
								<Skeleton variant="text" width="60%" height={18} />
								<Skeleton variant="text" width="90%" height={14} />
								<Skeleton variant="text" width="30%" height={12} />
							</SkeletonRowContent>
						</SkeletonRow>
					))}
				</SkeletonList>
			)}
			{filteredUserNotifications && filteredUserNotifications.length > 0 && (
				<NotificationListSection
					notifications={filteredUserNotifications.map(convertAlertToNotificationItem)}
					onNotificationClick={handleViewAlertClick}
					onDelete={handleDeleteAlert}
				/>
			)}
			{filteredUserNotifications && filteredUserNotifications.length === 0 && (
				<NoMarginEmptyCard title="All Clear" description="You have no notification right now." />
			)}
			<BottomModal
				title={modalMessage.title}
				body={modalMessage.description}
				type={modalMessage.errorType}
				actions={modalMessage.actions}
				open={hasValue(modalMessage.title)}
				onClose={handleCloseModal}
			/>
		</>
	);
};

export default NotificationSubPage;

const convertAlertToNotificationItem = (userNotification: UserNotification): NotificationItem => {
	return {
		id: userNotification.userNotificationId,
		title: userNotification.title,
		description: userNotification.body,
		datetime: getFormattedDate(userNotification.pushedDate),
		isUnRead: isUnreadNotification(userNotification.status)
	};
};

const StyledSearchInput = styled(SearchInput)`
	margin-top: 0;
	margin-bottom: 18px;
`;

const SkeletonList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const SkeletonRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 0;
	border-bottom: 1px solid #f0f0f0;
`;

const SkeletonRowContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 4px;
`;
